import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import {
  CreateDemandSnapshotService,
  CreateSnapshot,
  DemandSnapshot,
  Experience,
  LoadBonus,
  Region,
  Snapshots,
} from '../create-demand-snapshot.service';

@Component({
  selector: 'lohi-create-demand-snapshot',
  templateUrl: './create-demand-snapshot.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateDemandSnapshotComponent implements OnInit, OnDestroy {
  public experienceControl = new FormControl('');
  public bonusChecked = new FormControl(false);
  public destroy$$ = new Subject();
  public experienceTypes$: Observable<Experience[]>;
  public regions$: Observable<Region[]>;
  public demandSnapshots$: Observable<Snapshots>;
  public tableColumns: string[] = ['regionName', 'driverDemand', 'showIfUnprofitable', 'centsPerShift', 'expiresAt'];
  public snapshotForm: FormGroup;
  public selectedSnapshot: DemandSnapshot;
  public selectedExperienceId: string;

  constructor(private createDemandSnapShotService: CreateDemandSnapshotService, private fb: FormBuilder) {}

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public ngOnInit(): void {
    this.experienceTypes$ = this.createDemandSnapShotService.experienceTypes$;
    this.regions$ = this.createDemandSnapShotService.regions$;
    this.snapshotForm = this.fb.group({
      regionId: [null, [Validators.required]],
      driverDemand: [null, [Validators.required]],
      centsPerShift: [null, [Validators.required]],
      dateControl: [null, [Validators.required]],
      showIfUnprofitable: [false, [Validators.required]],
      optionalTitle: [null],
      optionalDescription: [null],
      bonusType: [null],
      bonusQuantity: [null],
      bonusExpiresAt: [null],
      maxCentsPerUser: [null],
    });
    this.demandSnapshots$ = this.experienceControl.valueChanges.pipe(
      switchMap((value) => {
        if (!value) {
          return of(null);
        }
        return this.createDemandSnapShotService.getDemandSnapshots$(value.id);
      }),
    );
    combineLatest([this.snapshotForm.get('regionId').valueChanges, this.demandSnapshots$])
      .pipe(takeUntil(this.destroy$$))
      .subscribe(([region, demand]) => {
        if (region) {
          this.selectedSnapshot = demand.snapshots.find((snapshot) => snapshot.regionId === region);
          if (this.selectedSnapshot) {
            this.snapshotForm.patchValue({ centsPerShift: this.selectedSnapshot.centsPerShift });
            this.snapshotForm.patchValue({ driverDemand: this.selectedSnapshot.driverDemand });
            this.snapshotForm.patchValue({ showIfUnprofitable: this.selectedSnapshot.showIfUnprofitable });
            this.snapshotForm.patchValue({ dateControl: this.selectedSnapshot.expiresAt });
          }
        }
      });
  }

  public async createNewSnapshot() {
    if (this.snapshotForm.invalid) {
      return;
    }
    const formValue = this.snapshotForm.value;
    const sendSnapshot: CreateSnapshot = {
      regionId: formValue.regionId,
      experienceTypeId: this.experienceControl.value.id,
      driverDemand: formValue.driverDemand,
      centsPerShift: formValue.centsPerShift,
      expiresAt: formValue.dateControl,
      showIfUnprofitable: formValue.showIfUnprofitable,
    };
    if (formValue.optionalTitle) {
      sendSnapshot.title = formValue.optionalTitle;
    }
    if (formValue.optionalDescription) {
      sendSnapshot.description = formValue.optionalDescription;
    }
    if (this.bonusChecked.value) {
      const loadBonus: LoadBonus = {
        type: formValue.bonusType,
        quantity: formValue.bonusQuantity,
        expiresAt: formValue.bonusExpiresAt,
        maxCentsPerUser: formValue.maxCentsPerUser,
      };
      sendSnapshot.loadBonus = loadBonus;
    }
    try {
      await this.createDemandSnapShotService.createSnapshot(sendSnapshot);
    } catch (e) {
      console.error(e);
    }
    this.snapshotForm.reset();
    const type = this.experienceControl.value;
    this.experienceControl.setValue(null);
    this.experienceControl.setValue(type);
  }
}
